import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { supabase, type Note, type NoteInsert, type NoteUpdate } from '../lib/supabase'

interface NotesState {
  notes: Note[]
  selectedNoteId: string | null
  isLoading: boolean
  error: string | null
  searchQuery: string
  filteredNotes: Note[]
}

interface NotesActions {
  // Data fetching
  fetchNotes: () => Promise<void>
  refreshNotes: () => Promise<void>
  
  // CRUD operations
  createNote: (title?: string) => Promise<Note | null>
  updateNote: (id: string, updates: Partial<NoteUpdate>) => Promise<void>
  deleteNote: (id: string) => Promise<void>
  
  // UI state
  selectNote: (id: string | null) => void
  setSearchQuery: (query: string) => void
  
  // Utility
  clearError: () => void
}

export type NotesStore = NotesState & NotesActions

export const useNotesStore = create<NotesStore>()(
  immer((set) => ({
    // Initial state
    notes: [],
    selectedNoteId: null,
    isLoading: false,
    error: null,
    searchQuery: '',
    filteredNotes: [],

    // Actions
    fetchNotes: async () => {
      set((state) => {
        state.isLoading = true
        state.error = null
      })

      try {
        const { data: session } = await supabase.auth.getSession()
        if (!session.session?.user) {
          throw new Error('User not authenticated')
        }

        const { data, error } = await supabase
          .from('notes')
          .select('*')
          .eq('user_id', session.session.user.id)
          .eq('is_trashed', false)
          .order('updated_at', { ascending: false })

        if (error) throw error

        set((state) => {
          state.notes = data || []
          state.filteredNotes = data || []
          state.isLoading = false
        })
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to fetch notes'
          state.isLoading = false
        })
      }
    },

    refreshNotes: async () => {
      // Lightweight refresh without loading state
      try {
        const { data: session } = await supabase.auth.getSession()
        if (!session.session?.user) return

        const { data, error } = await supabase
          .from('notes')
          .select('*')
          .eq('is_trashed', false)
          .eq('user_id', session.session.user.id)
          .order('updated_at', { ascending: false })

        if (error) throw error

        set((state) => {
          const selectedId = state.selectedNoteId
          state.notes = data || []
          
          // Update filtered notes based on current search
          if (state.searchQuery.trim() === '') {
            state.filteredNotes = state.notes
          } else {
            const query = state.searchQuery.toLowerCase()
            state.filteredNotes = state.notes.filter(note => 
              (note.title || '').toLowerCase().includes(query) ||
              note.content_plain?.toLowerCase().includes(query) ||
              note.excerpt?.toLowerCase().includes(query)
            )
          }
          
          // Preserve selection if note still exists
          if (selectedId && !state.notes.find(n => n.id === selectedId)) {
            state.selectedNoteId = state.notes.length > 0 ? state.notes[0].id : null
          }
        })
      } catch (error) {
        console.error('Failed to refresh notes:', error)
      }
    },

    createNote: async (title = 'Untitled') => {
      try {
        const { data: session } = await supabase.auth.getSession()
        if (!session.session?.user) {
          throw new Error('User not authenticated')
        }

        const newNote: NoteInsert = {
          user_id: session.session.user.id,
          title,
          content: '',
          content_plain: '',
          excerpt: null,
          word_count: 0,
          character_count: 0,
          reading_time: 0
        }

        const { data, error } = await supabase
          .from('notes')
          .insert(newNote)
          .select()
          .single()

        if (error) throw error

        set((state) => {
          state.notes.unshift(data)
          state.filteredNotes.unshift(data)
          state.selectedNoteId = data.id
        })

        return data
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to create note'
        })
        return null
      }
    },

    updateNote: async (id: string, updates: Partial<NoteUpdate>) => {
      try {
        // Calculate content metrics if content is being updated
        let processedUpdates = { ...updates }
        
        if (updates.content) {
          const plainText = updates.content.replace(/<[^>]*>/g, '') // Basic HTML strip
          const wordCount = plainText.trim().split(/\s+/).filter(Boolean).length
          const characterCount = plainText.length
          const readingTime = Math.ceil(wordCount / 200) // 200 WPM average
          const excerpt = plainText.slice(0, 150) + (plainText.length > 150 ? '...' : '')

          processedUpdates = {
            ...processedUpdates,
            content_plain: plainText,
            word_count: wordCount,
            character_count: characterCount,
            reading_time: readingTime,
            excerpt,
            updated_at: new Date().toISOString()
          }
        }

        const { data, error } = await supabase
          .from('notes')
          .update(processedUpdates)
          .eq('id', id)
          .select()
          .single()

        if (error) throw error

        set((state) => {
          const noteIndex = state.notes.findIndex(note => note.id === id)
          if (noteIndex !== -1) {
            state.notes[noteIndex] = data
          }
          
          const filteredIndex = state.filteredNotes.findIndex(note => note.id === id)
          if (filteredIndex !== -1) {
            state.filteredNotes[filteredIndex] = data
          }
        })
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to update note'
        })
      }
    },

    deleteNote: async (id: string) => {
      try {
        // Soft delete by moving to trash
        const { error } = await supabase
          .from('notes')
          .update({ 
            is_trashed: true,
            updated_at: new Date().toISOString()
          })
          .eq('id', id)

        if (error) throw error

        set((state) => {
          state.notes = state.notes.filter(note => note.id !== id)
          state.filteredNotes = state.filteredNotes.filter(note => note.id !== id)
          
          if (state.selectedNoteId === id) {
            state.selectedNoteId = state.notes.length > 0 ? state.notes[0].id : null
          }
        })
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to delete note'
        })
      }
    },

    selectNote: (id: string | null) => {
      set((state) => {
        state.selectedNoteId = id
      })
    },

    setSearchQuery: (query: string) => {
      set((state) => {
        state.searchQuery = query
        
        if (query.trim() === '') {
          state.filteredNotes = state.notes
        } else {
          const lowerQuery = query.toLowerCase()
          state.filteredNotes = state.notes.filter(note => 
            note.title?.toLowerCase().includes(lowerQuery) ||
            note.content_plain.toLowerCase().includes(lowerQuery)
          )
        }
      })
    },

    clearError: () => {
      set((state) => {
        state.error = null
      })
    }
  }))
)